'use client'

import { cloneElement, isValidElement, ReactNode, useCallback, useMemo, useEffect } from 'react'

type TokenType = 'en' | 'ko' | 'jp' | 'cn' | 'ar' | 'num' | 'punct' | 'space' | 'etc'

interface TokenInfo {
  text: string
  type: TokenType
}

const regexes: Record<TokenType, RegExp> = {
  en: /[A-Za-z]+/,
  ko: /[ㄱ-ㅎㅏ-ㅣ가-힣]+/,
  jp: /[\u3040-\u309F\u30A0-\u30FF]+/,
  cn: /[\u4E00-\u9FBF]+/,
  ar: /[\u0600-\u06ff]|[\u0750-\u077f]|[\ufb50-\ufc3f]|[\ufe70-\ufefc]+/,
  num: /\b[0-9]+\b/,
  punct: /[（）()\{\}\^\\\-&,;~:<>""'''/@%*+=#|，、。」.·←→↑↓\u2000-\u206F\u2E00-\u2E7F]+/,
  space: /\s+/,
  etc: /./,
}

// LRU 캐시 구현으로 개선
class LRUCache<K, V> {
  private cache = new Map<K, V>()

  constructor(private maxSize: number) {}

  get(key: K): V | undefined {
    const value = this.cache.get(key)
    if (value !== undefined) {
      // 최근 사용으로 이동
      this.cache.delete(key)
      this.cache.set(key, value)
    }
    return value
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      // 가장 오래된 항목 제거
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }
    this.cache.set(key, value)
  }

  clear(): void {
    this.cache.clear()
  }
}

// 전역 캐시를 LRU로 교체
const processCache = new LRUCache<string, ReactNode>(1000)

// 정규식 순서 최적화 (빈도 높은 순서로)
const regexOrder: TokenType[] = ['space', 'punct', 'en', 'ko', 'num', 'cn', 'jp', 'ar']

export default function Multilingual({ children }: { children: ReactNode }) {
  // 컴포넌트 언마운트 시 캐시 정리
  useEffect(() => {
    return () => {
      processCache.clear()
    }
  }, [])

  const tokenizeText = useCallback((text: string): TokenInfo[] => {
    // 더 효율적인 토큰화
    const tokens: TokenInfo[] = []
    let currentIndex = 0

    while (currentIndex < text.length) {
      let matched = false

      // 빈도 높은 패턴부터 확인
      for (const type of regexOrder) {
        const regex = regexes[type]
        const match = text.slice(currentIndex).match(regex)

        if (match && match.index === 0) {
          tokens.push({
            text: match[0],
            type: type === 'etc' ? 'etc' : type,
          })
          currentIndex += match[0].length
          matched = true
          break
        }
      }

      // 매칭되지 않은 문자는 etc로 처리
      if (!matched) {
        tokens.push({
          text: text[currentIndex],
          type: 'etc',
        })
        currentIndex++
      }
    }

    return tokens
  }, [])

  const breakByWord = useCallback(
    (text: string): ReactNode => {
      // 캐시에서 확인
      const cached = processCache.get(text)
      if (cached) {
        return cached
      }

      const tokens = tokenizeText(text)

      const result = tokens
        .filter(token => token.text !== '')
        .map((token, index) => {
          const className = `word-${token.type}`

          return (
            <span key={`${index}-${token.text}-${token.type}`} className={className}>
              {token.text}
            </span>
          )
        })

      // LRU 캐시에 저장
      processCache.set(text, result)

      return result
    },
    [tokenizeText]
  )

  const breakByWordRecursively = useCallback(
    (node: ReactNode): ReactNode => {
      if (typeof node === 'string') {
        return breakByWord(node)
      } else if (Array.isArray(node)) {
        return node.map((child, index) => <span key={index}>{breakByWordRecursively(child)}</span>)
      } else if (isValidElement(node)) {
        const { children, ...props } = node.props as { children: ReactNode; [key: string]: any }
        const newChildren = breakByWordRecursively(children)
        return cloneElement(node, props, newChildren)
      } else {
        return node
      }
    },
    [breakByWord]
  )

  const processedChildren = useMemo(
    () => breakByWordRecursively(children),
    [breakByWordRecursively, children]
  )

  return <>{processedChildren}</>
}
